import { ArrowLeft } from 'lucide-react'
import { Children, isValidElement, ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useTranslator from '@/hooks/translation'

import { CompanySelection } from '../company-selection'

interface HeaderProps {
  children: ReactNode
  backLink?: string
}

function RootHeader({ children, backLink = '/' }: HeaderProps) {
  const childArray = Children.toArray(children)

  const haderTitle = childArray.find(
    (child): child is ReactElement<unknown> =>
      isValidElement(child) && child.type === HeaderTitle,
  )

  const headerAction = childArray.find(
    (child): child is ReactElement<unknown> =>
      isValidElement(child) && child.type === HeaderAction,
  )

  const headerBreadcrumbs = childArray.find(
    (child): child is ReactElement<unknown> =>
      isValidElement(child) && child.type === HeaderBreadcrumbs,
  )

  const { getTranslation } = useTranslator()

  const navigate = useNavigate()

  function handleNavigateBack(): void {
    navigate(backLink)
  }

  return (
    <header className="w-full select-none border-b">
      <div className="flex h-full w-full items-center bg-background shadow-md">
        <div className="flex w-72 shrink-0 items-center gap-2 py-4 pl-4 pr-3">
          <Tooltip>
            <TooltipTrigger>
              <Button
                variant="outline"
                size="icon"
                className="p-2 [&_svg]:size-5"
                onClick={handleNavigateBack}
              >
                <ArrowLeft />
              </Button>
            </TooltipTrigger>

            <TooltipContent>
              <p>{getTranslation('common.back')}</p>
            </TooltipContent>
          </Tooltip>

          <CompanySelection />
        </div>

        <Separator orientation="vertical" className="mr-4" />

        {haderTitle && (
          <div className="flex items-center gap-2">
            <h1 className="mx-2 text-sm font-bold md:text-base">
              {haderTitle}
            </h1>
          </div>
        )}

        {headerBreadcrumbs && <>{headerBreadcrumbs}</>}

        <div className="ml-auto mr-4 flex items-center gap-4">
          {headerAction}
        </div>
      </div>
    </header>
  )
}

function HeaderTitle({ children }: { children: ReactNode }) {
  return <>{children}</>
}

function HeaderBreadcrumbs({ children }: { children: ReactNode }) {
  return <>{children}</>
}

function HeaderAction({ children }: { children: ReactNode }) {
  return <>{children}</>
}

export const Header = Object.assign(RootHeader, {
  Title: HeaderTitle,
  Action: HeaderAction,
  Breadcrumbs: HeaderBreadcrumbs,
})
